/** @typedef {{ lng: number, lat: number, zoom?: number | null }} MapboxUrlView */

function roundCoord(value, decimals) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * True for `#/map`, `#/map/`, and any `#/map/...` coordinate path.
 * @param {string} hash
 */
export function isMapEntryHash(hash) {
  if (!hash || !hash.startsWith("#/map")) return false;
  const normalized = hash.replace(/\/+$/, "") || hash;
  if (normalized === "#/map") return true;
  return normalized.startsWith("#/map/");
}

/**
 * @param {string} hash
 * @returns {{ isMap: boolean, view: MapboxUrlView | null }}
 */
export function parseMapUrlHash(hash) {
  if (!isMapEntryHash(hash)) return { isMap: false, view: null };

  const normalized = hash.replace(/\/+$/, "") || hash;
  if (normalized === "#/map") return { isMap: true, view: null };

  const segments = normalized.slice("#/map/".length).split("/").filter(Boolean);
  if (segments.length < 2) return { isMap: true, view: null };

  const lng = parseFloat(segments[0]);
  const lat = parseFloat(segments[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return { isMap: true, view: null };
  }

  if (segments.length >= 3) {
    const zoom = parseFloat(segments[2]);
    if (Number.isFinite(zoom)) {
      return { isMap: true, view: { lng, lat, zoom } };
    }
  }

  return { isMap: true, view: { lng, lat, zoom: null } };
}

/**
 * @param {MapboxUrlView | null | undefined} view
 * @returns {string}
 */
export function formatMapUrlHash(view) {
  if (!view) return "#/map";
  const lng = roundCoord(view.lng, 5);
  const lat = roundCoord(view.lat, 5);
  if (view.zoom == null || !Number.isFinite(view.zoom)) {
    return `#/map/${lng}/${lat}`;
  }
  const zoom = roundCoord(view.zoom, 2);
  return `#/map/${lng}/${lat}/${zoom}`;
}

/**
 * @param {import('mapbox-gl').Map | null | undefined} map
 * @returns {MapboxUrlView | null}
 */
export function getMapboxViewFromInstance(map) {
  if (!map || typeof map.getCenter !== "function" || typeof map.getZoom !== "function") {
    return null;
  }

  const center = map.getCenter();
  const zoom = map.getZoom();
  if (
    !center ||
    !Number.isFinite(center.lng) ||
    !Number.isFinite(center.lat) ||
    !Number.isFinite(zoom)
  ) {
    return null;
  }

  return { lng: center.lng, lat: center.lat, zoom };
}

/**
 * @param {import('mapbox-gl').Map | null | undefined} map
 * @param {MapboxUrlView | null | undefined} view
 */
export function applyMapboxViewToInstance(map, view) {
  if (!map || !view || typeof map.jumpTo !== "function") return;
  const opts = { center: [view.lng, view.lat] };
  if (view.zoom != null && Number.isFinite(view.zoom)) {
    opts.zoom = view.zoom;
  }
  map.jumpTo(opts);
}
